/*
 * @forgerock/ping-javascript-sdk
 *
 * index.ts
 *
 * Copyright (c) 2024 - 2025 Ping Identity Corporation. All rights reserved.
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { callbackType } from '@forgerock/sdk-types';
/**
 * @class QRCode - A utility class for handling QR Code steps
 *
 * Example:
 *
 * ```js
 * const isQRCodeStep = QRCode.isQRCodeStep(step);
 * let qrCodeData;
 * if (isQRCodeStep) {
 *   qrCodeData = QRCode.getQRCodeData(step);
 * }
 * ```
 */
export class QRCode {
    /**
     * @method isQRCodeStep - determines if step contains QR Code callbacks
     * @param {JourneyStep} step - step object from AM response
     * @returns {boolean}
     */
    static isQRCodeStep(step) {
        const hiddenValueCb = step.getCallbacksOfType(callbackType.HiddenValueCallback);
        // QR Codes step should have at least one HiddenValueCallback
        if (hiddenValueCb.length === 0) {
            return false;
        }
        return !!this.getQRCodeURICb(hiddenValueCb);
    }
    /**
     * @method getQRCodeData - gets the necessary information from the QR Code callbacks
     * @param {JourneyStep} step - step object from AM response
     * @returns {QRCodeData}
     */
    static getQRCodeData(step) {
        const hiddenValueCb = step.getCallbacksOfType(callbackType.HiddenValueCallback);
        // QR Codes step should have at least one HiddenValueCallback
        if (hiddenValueCb.length === 0) {
            throw new Error('QR Code step must contain a HiddenValueCallback. Use `QRCode.isQRCodeStep` to guard.');
        }
        const qrCodeURICb = this.getQRCodeURICb(hiddenValueCb);
        const outputValue = qrCodeURICb ? qrCodeURICb.getOutputValue('value') : '';
        const qrCodeUse = typeof outputValue === 'string' && outputValue.includes('otpauth://') ? 'otp' : 'push';
        const messageCbs = step.getCallbacksOfType(callbackType.TextOutputCallback);
        const displayMessageCb = messageCbs.find((cb) => {
            const textOutputCallback = cb;
            return textOutputCallback.getMessageType() !== '4';
        });
        return {
            message: displayMessageCb ? displayMessageCb.getMessage() : '',
            use: qrCodeUse,
            uri: typeof outputValue === 'string' ? outputValue : '',
        };
    }
    static getQRCodeURICb(hiddenValueCbs) {
        // Look for a HiddenValueCallback with an OTP URI
        return hiddenValueCbs.find((cb) => {
            const outputValue = cb.getOutputValue('value');
            if (typeof outputValue === 'string') {
                return outputValue?.includes('otpauth://') || outputValue?.includes('pushauth://');
            }
            return false;
        });
    }
}
